import { Laptop, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTheme } from "@/hooks/useTheme";
import type { ThemePreference } from "@/store/theme/themeSlice";

const NEXT_PREFERENCE: Record<ThemePreference, ThemePreference> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const ICON_BY_PREFERENCE: Record<ThemePreference, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Laptop,
};

const LABEL_BY_PREFERENCE: Record<ThemePreference, string> = {
  light: "Light theme",
  dark: "Dark theme",
  system: "Matching system theme",
};

/**
 * Exercises the Shadcn `Button` primitive, the color tokens it reads
 * (`bg-background`, `text-foreground` via `variant="outline"`), and
 * `useTheme()` (src/hooks/useTheme.ts) together in one small, real control —
 * not a page. Cycles light → dark → system on each click; the icon and
 * accessible label always reflect the *stated* preference, not the
 * resolved theme, so a "system" choice reads as "system" even when it
 * currently resolves to dark.
 */
export function ThemeToggle() {
  const { themePreference, setThemePreference } = useTheme();
  const Icon = ICON_BY_PREFERENCE[themePreference];
  const label = `Theme: ${LABEL_BY_PREFERENCE[themePreference]}. Click to change.`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          onClick={() => setThemePreference(NEXT_PREFERENCE[themePreference])}
        >
          <Icon aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
